import * as React from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { GROUPS_KEY, fetchGroups } from './groupQueries'

/**
 * Wstrzykuje `groups` i `getGroups` z react-query do kontenera zakładki.
 *
 * Zapytanie tylko obserwuje cache (`enabled: false`) - pobranie zleca ekran, tak jak
 * wcześniej wołał thunka. Dzięki temu `tenantId` nadal decyduje o tym, co przyjdzie,
 * a ekran, który o listę nie prosi, widzi pustą - dokładnie jak przy slice'ie.
 *
 * Przepuszcza ref: panel administracyjny sięga po instancję zakładki, żeby wywołać
 * na niej `removeCheckedItems()`.
 */
export function withGroups<P extends object>(Wrapped: React.ComponentType<P>) {
  const WithGroups = React.forwardRef<unknown, Omit<P, 'groups' | 'getGroups'>>(
    (props, ref) => {
      const queryClient = useQueryClient()
      const { data: groups = [] } = useQuery({
        queryKey: GROUPS_KEY,
        queryFn: () => fetchGroups(),
        enabled: false,
      })

      // Thunk zwracał `response.status === 200`; ekrany sprawdzają tylko prawdziwość.
      const getGroups = React.useCallback(
        async (tenantId?: string) => {
          await queryClient.fetchQuery({
            queryKey: GROUPS_KEY,
            queryFn: () => fetchGroups(tenantId),
          })
          return true
        },
        [queryClient],
      )

      return (
        <Wrapped
          ref={ref as any}
          {...(props as unknown as P)}
          groups={groups}
          getGroups={getGroups}
        />
      )
    },
  )

  WithGroups.displayName = `withGroups(${
    (Wrapped as any).displayName || (Wrapped as any).name || 'Component'
  })`

  return WithGroups
}
